/**
 * Correcting a milestone after it exists.
 *
 * Until this dialog a milestone was write-once: the toolbar could add one and
 * the row menu could delete it, and a typo in the name or a date agreed one week
 * later meant deleting the row and typing it again - which loses the only thing
 * a milestone has besides its name, its place in the order. `updateMilestone`
 * has been exported and unused since the section was written.
 *
 * Three fields, and deliberately not the tick: `done` belongs to the checkbox
 * on the row, which is one click and already shared between the two screens
 * (`useMilestoneToggle`). A second way to set it inside a dialog would be a
 * second thing to keep in step. The start date is here and NOT in the list's
 * quick-add for the reason written at that form: this is where both dates are
 * settled, so this is where each can be held against the other.
 *
 * Only the changed fields are sent. `sortOrder` is on the same record and is
 * written by the up/down buttons in the list, so a full-object PATCH from here
 * would quietly hand the row back its old position.
 *
 * ## A start after its own deadline: refused, and refused BEFORE it is typed
 *
 * The pair is nonsense in every reading, and nothing downstream would catch it -
 * a timeline drawing a bar from these two would draw one of negative length. The
 * refusal is the pickers themselves: the start calendar stops at the deadline
 * and the deadline calendar starts at the start, so the bad pair is never
 * offered. That is chosen over rejecting it at Save for two reasons. A rule you
 * only meet by breaking it is a rule learnt at the cost of the operator's work,
 * and a Save that validates a field the operator did not touch would lock an
 * imported row out of a rename - the importer and the API can both write the
 * inverted pair, and this dialog is the only place it can be undone. Each field
 * is still clearable, so a row that arrives inverted always has a way out.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { updateMilestone, type Milestone } from "../../../lib/projects";
import { Btn, Modal, useToast } from "../../ui";
import { Field } from "../_shared/formBits";
import { toIso } from "./lib";

/** An ISO instant back into a date field's value. The inverse of `toIso`. */
function dateInput(s: string | null): string {
  return s ? s.slice(0, 10) : "";
}

/**
 * A date field's value as the bound for the other field's calendar.
 *
 * Parsed at local midnight, the way `DatePicker` parses its own value: a bound
 * built with `new Date("2026-03-10")` would be UTC midnight and would rule out
 * the 10th itself for anyone east of Greenwich. An empty field bounds nothing.
 */
function boundary(v: string): Date | undefined {
  if (!v) return undefined;
  const d = new Date(v + "T00:00:00");
  return Number.isNaN(d.getTime()) ? undefined : d;
}

export function MilestoneEditModal({
  milestone,
  onClose,
  onSaved,
}: {
  milestone: Milestone;
  onClose: () => void;
  onSaved: (m: Milestone) => void;
}) {
  const t = useT();
  const toast = useToast();
  const { locale } = useI18n();
  const [name, setName] = useState(milestone.name);
  const [start, setStart] = useState(dateInput(milestone.startDate));
  const [due, setDue] = useState(dateInput(milestone.dueDate));
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    const patch: Record<string, unknown> = {};
    if (trimmed !== milestone.name) patch.name = trimmed;
    // `toIso` answers undefined for an empty field, which a PATCH reads as "do
    // not touch". Clearing a date has to say null out loud.
    const nextStart = toIso(start) ?? null;
    if (dateInput(nextStart) !== dateInput(milestone.startDate)) patch.startDate = nextStart;
    const nextDue = toIso(due) ?? null;
    if (dateInput(nextDue) !== dateInput(milestone.dueDate)) patch.dueDate = nextDue;

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    setBusy(true);
    try {
      onSaved(await updateMilestone(milestone.id, patch));
      onClose();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("projects.milestoneEdit")} label={t("projects.milestoneEdit")} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={t("projects.milestoneName")}>
          <label className="crm-inp">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects.milestoneName")}
              autoComplete="off"
              required
            />
          </label>
        </Field>

        {/* Both dates on one line: the pair is read together, and a start date
            on its own row reads as a second, unrelated question. */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Field label={t("projects.milestoneStart")}>
              <DatePicker
                value={start}
                onChange={setStart}
                locale={locale}
                placeholder={t("projects.milestoneStart")}
                toDate={boundary(due)}
              />
            </Field>
          </div>
          <div>
            <Field label={t("projects.deadline")}>
              <DatePicker
                value={due}
                onChange={setDue}
                locale={locale}
                placeholder={t("projects.deadline")}
                fromDate={boundary(start)}
              />
            </Field>
          </div>
        </div>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !name.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
